import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Servicos() {
  const navigate = useNavigate();

  useEffect(() => {
    // Update page title and meta description for SEO
    document.title = 'Serviços de Contabilidade - N&N Soluções Empresariais | BPO Financeiro e Consultoria';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Serviços completos de contabilidade, BPO financeiro, consultoria empresarial, departamento pessoal e declaração de impostos. Soluções personalizadas para sua empresa.');
    }

    // Add structured data for services page
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Serviços de Contabilidade N&N",
      "description": "Serviços completos de contabilidade e consultoria empresarial",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/servicos`,
      "provider": {
        "@type": "Organization",
        "name": "N&N Soluções Empresariais"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Catálogo de Serviços",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Contabilidade Completa",
              "description": "Gestão contábil completa com foco em reduzir estresse e garantir melhores opções tributárias"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "BPO Financeiro",
              "description": "Organização completa de documentos financeiros com metodologia especializada"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Consultoria Empresarial",
              "description": "Abertura de empresas e consultoria societária com atendimento personalizado"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Departamento Pessoal",
              "description": "Gestão completa de recursos humanos e folha de pagamento"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Consultoria Financeira",
              "description": "Orientação financeira especializada para crescimento empresarial"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Declaração de Impostos",
              "description": "Preparação e envio de declarações fiscais para pessoas físicas e jurídicas"
            }
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.text.includes('Serviços de Contabilidade')) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="bg-white shadow-sm fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/3e6f8fcc8f2630e876a6bece5f75fd5d/1e6fcf1846e59e804b7269319d1935e6.png" 
                alt="N&N Soluções Empresariais" 
                className="h-16 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>
            <nav className="hidden md:flex space-x-8">
              <button onClick={() => navigate('/')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }}>Home</button>
              <button onClick={() => navigate('/servicos')} className="font-semibold cursor-pointer" style={{ color: '#72142B' }}>Serviços</button>
              <button onClick={() => navigate('/sobre')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }}>Sobre</button>
              <button onClick={() => navigate('/contato')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }}>Contato</button>
              <button onClick={() => navigate('/agendamento')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }}>Agendamento</button>
            </nav>
            <a 
              href="https://wa.me/5562993911090"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white px-6 py-2 rounded-full hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#72142B' }}
            >
              Fale Conosco
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20professional%20accounting%20office%20with%20elegant%20workspace%2C%20financial%20documents%20and%20computers%2C%20sophisticated%20business%20environment%20with%20warm%20lighting%2C%20clean%20organized%20desk%20setup%20conveying%20expertise%20and%20trust%20in%20financial%20services&width=1920&height=800&seq=servicos-hero-bg&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#72142B]/80 via-[#72142B]/40 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
            Nossos Serviços
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
            Soluções completas em contabilidade e finanças para pessoas físicas e jurídicas
          </p>
          <a 
            href="https://wa.me/5562993911090"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 hover:transform hover:scale-105 whitespace-nowrap cursor-pointer"
            style={{ backgroundColor: '#FAF7F2', color: '#72142B' }}
          >
            Solicitar Orçamento
          </a>
        </div>
      </section>

      {/* Serviços Principais */}
      <section className="py-20" style={{ backgroundColor: '#FAF7F2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#72142B' }}>
              Serviços Principais
            </h2>
            <p className="text-xl text-gray-600">
              Expertise e dedicação em cada área de atuação
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#72142B' }}>
                <i className="ri-calculator-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>Contabilidade</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Quando você escolhe nossa equipe para lidar com a contabilidade de sua empresa, recebe mais do que experiência e visão. Recebe parceiros comprometidos em reduzir seu estresse e garantir as melhores opções tributárias.
              </p>
              <a 
                href="https://wa.me/5562993911090"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-white px-6 py-3 rounded-full hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: '#72142B' }}
              >
                Solicitar orçamento
              </a>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#72142B' }}>
                <i className="ri-line-chart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>BPO Financeiro</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Nossa equipe especializada em BPO Financeiro garante a organização de todos os documentos financeiros, mantendo-os atualizados com regularidade.
              </p>
              <a 
                href="https://wa.me/5562993911090"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-white px-6 py-3 rounded-full hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: '#72142B' }}
              >
                Agendar reunião
              </a>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#72142B' }}>
                <i className="ri-building-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>Societário</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Escolher a N&N Soluções Empresariais na abertura de seu negócio é investir em segurança e estrutura sólida. Nossa equipe personaliza o serviço conforme sua necessidade.
              </p>
              <a 
                href="https://wa.me/5562993911090"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-white px-6 py-3 rounded-full hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: '#72142B' }}
              >
                Consultar abertura
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Serviços Especializados */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: '#72142B' }}>
              Serviços Especializados
            </h2>
            <p className="text-xl text-gray-600">Soluções personalizadas para cada necessidade</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div 
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
              onClick={() => navigate('/servicos/gestao-contabil')}
            >
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#72142B' }}>
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>
                Gestão Contábil
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Segurança e proteção total da contabilidade da sua empresa.
              </p>
              <div className="flex items-center font-semibold group-hover:translate-x-2 transition-transform duration-300" style={{ color: '#72142B' }}>
                Saiba mais <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </div>

            <div 
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
              onClick={() => navigate('/servicos/gestao-fiscal')}
            >
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#72142B' }}>
                <i className="ri-search-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>
                Gestão Fiscal
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Soluções para minimizar impostos e manter uma vida financeira saudável.
              </p>
              <div className="flex items-center font-semibold group-hover:translate-x-2 transition-transform duration-300" style={{ color: '#72142B' }}>
                Saiba mais <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </div>

            <div 
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
              onClick={() => navigate('/servicos/relatorios-financeiros')}
            >
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#72142B' }}>
                <i className="ri-file-chart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>
                Relatórios Financeiros
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Relatórios claros e eficientes para decisões assertivas.
              </p>
              <div className="flex items-center font-semibold group-hover:translate-x-2 transition-transform duration-300" style={{ color: '#72142B' }}>
                Saiba mais <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </div>

            <div 
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
              onClick={() => navigate('/servicos/gestao-pessoas')}
            >
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#72142B' }}>
                <i className="ri-team-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>
                Gestão de Pessoas
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Foco na atualização legal e na boa gestão de equipe.
              </p>
              <div className="flex items-center font-semibold group-hover:translate-x-2 transition-transform duration-300" style={{ color: '#72142B' }}>
                Saiba mais <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Serviços com Agendamento */}
      <section className="py-20" style={{ backgroundColor: '#FAF7F2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: '#72142B' }}>
              Agende sua Consulta
            </h2>
            <p className="text-xl text-gray-600">Serviços disponíveis para agendamento online</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20HR%20department%20workspace%20with%20documents%20and%20computer%20showing%20employee%20management%20system%2C%20modern%20office%20environment%2C%20clean%20organized%20desk%20with%20personnel%20files%2C%20warm%20professional%20lighting%20conveying%20efficiency%20and%20organization&width=300&height=200&seq=dp-service-page&orientation=landscape"
                alt="Departamento Pessoal"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <h3 className="text-xl font-bold mb-4" style={{ color: '#72142B' }}>Departamento Pessoal</h3>
              <a 
                href="https://wa.me/5562993911090"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-white px-6 py-2 rounded-full hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: '#72142B' }}
              >
                Solicitar agendamento
              </a>
            </div>

            <div className="text-center bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105">
              <img
                src="https://readdy.ai/api/search-image?query=Financial%20consultation%20meeting%20with%20professional%20advisor%20and%20client%20reviewing%20investment%20documents%20and%20charts%2C%20modern%20office%20setting%2C%20calculator%20and%20financial%20reports%20on%20desk%2C%20professional%20atmosphere%20conveying%20trust%20and%20expertise&width=300&height=200&seq=consultoria-service-page&orientation=landscape"
                alt="Consultoria Financeira"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <h3 className="text-xl font-bold mb-4" style={{ color: '#72142B' }}>Consultoria Financeira</h3>
              <a 
                href="https://wa.me/5562993911090"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-white px-6 py-2 rounded-full hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: '#72142B' }}
              >
                Solicitar agendamento
              </a>
            </div>

            <div className="text-center bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105">
              <img
                src="https://readdy.ai/api/search-image?query=Accounting%20workspace%20with%20ledger%20books%2C%20calculator%2C%20financial%20statements%20and%20computer%20showing%20accounting%20software%2C%20organized%20professional%20desk%20setup%2C%20warm%20lighting%20creating%20trustworthy%20business%20environment&width=300&height=200&seq=contabilidade-service-page&orientation=landscape"
                alt="Contabilidade"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <h3 className="text-xl font-bold mb-4" style={{ color: '#72142B' }}>Contabilidade</h3>
              <a 
                href="https://wa.me/5562993911090"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-white px-6 py-2 rounded-full hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: '#72142B' }}
              >
                Solicitar agendamento
              </a>
            </div>

            <div className="text-center bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-30 hover:transform hover:scale-105">
              <img
                src="https://nensolucoes.com.br/impostos-service-page.jpg"
                alt="Declaração de Impostos"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <h3 className="text-xl font-bold mb-4" style={{ color: '#72142B' }}>Declaração de Impostos</h3>
              <a 
                href="https://wa.me/5562993911090"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-white px-6 py-2 rounded-full hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: '#72142B' }}
              >
                Solicitar agendamento
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20"
        style={{
          background: `linear-gradient(135deg, #72142B 0%, #8B1538 50%, #72142B 100%)`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Precisa de ajuda para escolher?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            Nossa equipe está pronta para orientar você na escolha do melhor serviço para sua necessidade
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/contato')}
              className="px-8 py-4 rounded-full hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer text-lg font-semibold"
              style={{ backgroundColor: '#FAF7F2', color: '#72142B' }}
            >
              Fale conosco
            </button>
            <a 
              href="https://wa.me/5562993911090"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block border-2 border-white text-white px-8 py-4 rounded-full hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer text-lg font-semibold"
              style={{ borderColor: '#FAF7F2', color: '#FAF7F2' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#FAF7F2';
                e.currentTarget.style.color = '#72142B';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#FAF7F2';
              }}
            >
              Agendar consulta
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white py-16" style={{ backgroundColor: '#72142B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
                N&N Soluções Empresariais
              </h3>
              <p className="text-white/80 mb-6">
                Simplificando sua contabilidade para você focar no que realmente importa.
              </p>
              <div className="space-y-2">
                <p className="flex items-center text-gray-300">
                  <i className="ri-phone-line mr-3"></i>
                  WhatsApp: (62) 99391-1090
                </p>
                <p className="flex items-center text-gray-300">
                  <i className="ri-mail-line mr-3"></i>
                  contato@nnsolucoes.com.br
                </p>
                <p className="flex items-center text-gray-300">
                  <i className="ri-map-pin-line mr-3"></i>
                  Av. Goiás, 606 - Setor Central, Goiânia - GO
                </p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Links Úteis</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/')} className="text-white/80 hover:text-white transition-colors cursor-pointer">Home</button></li>
                <li><button onClick={() => navigate('/servicos')} className="text-white/80 hover:text-white transition-colors cursor-pointer">Serviços</button></li>
                <li><button onClick={() => navigate('/sobre')} className="text-white/80 hover:text-white transition-colors cursor-pointer">Sobre</button></li>
                <li><button onClick={() => navigate('/contato')} className="text-white/80 hover:text-white transition-colors cursor-pointer">Contato</button></li>
                <li><button onClick={() => navigate('/agendamento')} className="text-white/80 hover:text-white transition-colors cursor-pointer">Agendamento</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Nossos Serviços</h4>
              <ul className="space-y-2">
                <li><span className="text-white/80">Contabilidade</span></li>
                <li><span className="text-white/80">BPO Financeiro</span></li>
                <li><span className="text-white/80">Departamento Pessoal</span></li>
                <li><span className="text-white/80">Consultoria Financeira</span></li>
                <li><span className="text-white/80">Declaração de Impostos</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 mt-12 pt-8 text-center">
            <p className="text-white/80">
              © 2024 N&N Soluções Empresariais. Todos os direitos reservados. | 
              <a href="https://readdy.ai/?origin=logo" className="hover:text-white ml-1 cursor-pointer" style={{ color: '#FAEBEA' }}>
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
